import type { IJodit } from 'jodit/types/types';

/**
 * ID attribute for the injected body-style tag inside the Jodit iframe head. Used for idempotent removal before
 * re-injection and for clean export.
 */
export const BODY_STYLE_TAG_ID = '_jodit_body_meta_style';

/**
 * Extract the `style` attribute value from a `<body>` tag in an HTML string.
 *
 * @param html - Raw HTML string (full document or fragment)
 * @returns The inline style string (e.g. `background-color: #fff; font-family: Arial;`), or `''` if absent.
 */
export function extractBodyStyle(html: string): string {
  if (!html) {
    return '';
  }
  const match = /<body[^>]*\sstyle="([^"]*)"/i.exec(html);
  return match?.[1] ?? '';
}

/**
 * Inject `<body>` inline styles into the Jodit iframe `<head>` so the WYSIWYG view renders email template backgrounds,
 * fonts, and margins correctly.
 *
 * **Idempotent**: removes any existing tag with `id="_jodit_body_meta_style"` before re-injecting, so calling this
 * multiple times is safe.
 *
 * **CSS targeting**: styles target `body` directly. In standard iframe mode the body carries `class="jodit-wysiwyg"`
 * and `body { … }` applies. In `editHTMLDocumentMode` (used by `EMAIL_JODIT_CONFIG` for email templates), Jodit
 * replaces the entire iframe document via `document.write()` — the email's own `<body>` element is loaded and never
 * receives the `jodit-wysiwyg` class, so `body.jodit-wysiwyg` would not match. Targeting `body` directly works
 * correctly in both modes.
 *
 * @param editor - Active Jodit editor instance
 * @param bodyStyle - CSS property string extracted from `<body style="...">` (may be empty)
 */
export function applyBodyStyleToIframe(editor: IJodit, bodyStyle: string): void {
  const iDoc = editor.editorDocument;
  if (!iDoc) {
    return;
  }

  const existing = iDoc.getElementById(BODY_STYLE_TAG_ID);
  if (existing) {
    existing.remove();
  }

  if (!bodyStyle) {
    return;
  }

  const styleEl = iDoc.createElement('style');
  styleEl.id = BODY_STYLE_TAG_ID;
  styleEl.textContent = `body { ${bodyStyle} }`;
  iDoc.head.appendChild(styleEl);
}

/**
 * ID attribute for the re-injected `iframeStyle` tag inside the Jodit iframe head. Used for idempotent removal before
 * re-injection and when reading the clean editor value.
 */
export const IFRAME_STYLE_TAG_ID = '_jodit_iframe_style';

/**
 * Re-inject the Jodit `iframeStyle` config CSS into the editor iframe head.
 *
 * In `editHTMLDocumentMode`, Jodit replaces the entire iframe document via `document.write()` whenever content is set —
 * wiping any styles Jodit injected during initialisation (including those from the `iframeStyle` config option). This
 * function re-injects those baseline styles (body margins, table layout, image constraints) so the WYSIWYG area is
 * always styled correctly, even when the content has no `<body style="...">` attribute.
 *
 * **Idempotent**: removes any existing `id="_jodit_iframe_style"` tag before re-injecting.
 *
 * @param editor - Active Jodit editor instance
 * @param css - Full CSS string from the `iframeStyle` config option (complete CSS rules)
 */
export function applyIframeStyleToDocument(editor: IJodit, css: string): void {
  const iDoc = editor.editorDocument;
  if (!iDoc || !css) {
    return;
  }

  const existing = iDoc.getElementById(IFRAME_STYLE_TAG_ID);
  if (existing) {
    existing.remove();
  }

  const styleEl = iDoc.createElement('style');
  styleEl.id = IFRAME_STYLE_TAG_ID;
  styleEl.textContent = css;
  iDoc.head?.appendChild(styleEl);
}
